import React, { useState, useEffect, useRef } from 'react';
import WebFont from 'webfontloader';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Store from '@apply/store';
import { Header } from '../common_v2/components/Header';
import { useAuthentication } from './LoginPageV2';
import { validEmail, validPhone, fonts, isMobileScreen } from '@apply/ui';
import { UserStore } from '@apply/store';
import debounce from 'lodash.debounce';
import { TextBox } from '../../../../../packages/components/src/components/controls/TextBox/TextBox';
import { RequiredRule } from 'devextreme-react/validator';
import { ValidationGroup } from 'devextreme-react/validation-group';
import {
    ToggleButtonItem,
    ToggleButton,
    Panel,
    PanelTheme,
    PanelBorderRadius,
    ButtonTheme,
    Button,
    EmailBox,
} from '@apply/components';
import { HomePageLink } from '../HomePageLink';
import { SignaturePage } from '@apply/components/src/components/controls/SignaturePage';

export interface Actions {
    validateEmail?: (email: string, userType: UserStore.Types.UserType) => Promise<UserStore.Types.ValidateEmailResult>;
    registerPrincipal?: (
        id: string,
        email: string,
        firstName: string,
        middleName: string,
        lastName: string,
        mobilePhone: string,
        secondaryEmail: string,
        sSignFontId: number,
        ipAddress: string
    ) => Promise<UserStore.Types.RegisterUserResult>;
    requestBrokerProfile?: (
        email: string,
        firstName: string,
        middleName: string,
        lastName: string,
        mobilePhone: string
    ) => Promise<UserStore.Types.RegisterUserResult>;
    onNeedIpAddress?: () => Promise<string>;
    getUserProfile?: (id: string, token: string) => Promise<Store.CrossStore.UserProfileResult>;
}

export interface RegisterPageProps {
    actions?: Actions;
}

const getElectronicRecordsHtml = async () => await Store.CrossStore.getElectronicRecordsHtml();

export const RegisterPageV2: React.FC<RegisterPageProps> = ({ actions }) => {
    const [email, setEmail] = useState<string>('');
    const [firstName, setFirstName] = useState<string>('');
    const [middleName, setMiddleName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [mobilePhone, setMobilePhone] = useState<string>('');
    const [secondaryEmail, setSecondaryEmail] = useState<string>('');
    const [acknowledgeNoPersonalLoans, setAcknowledgeNoPersonalLoans] = useState<boolean>(false);
    const [agreeToEsignAgreement, setAgreeToEsignAgreement] = useState<boolean>(false);

    const [submitting, setSubmitting] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const [successful, setSuccessful] = useState<boolean>(false);

    const [emailUnavailable, setEmailUnavailable] = useState<boolean>(false);
    const [accountExists, setAccountExists] = useState<boolean>(false);
    const [targetBaseUrl, setTargetBaseUrl] = useState<string>(window.location.origin);

    const [ipAddress, setIpAddress] = useState<string>('');
    const [esignHtml, set_esignHtml] = useState<string | TrustedHTML>('');
    const [agreementOpen, setAgreementOpen] = useState<boolean>(false);
    const [eSignFontId, setESignFontId] = useState<number>(1);

    const navigate = useNavigate();

    //0 = Not set, 1 = Applicant, 2 == Broker
    const enum RegisterType {
        NotSet = 0,
        Applicant = 1,
        Broker = 2,
    }
    const [registerType, set_registerType] = useState<RegisterType>(RegisterType.NotSet);
    const [selectedItems, set_selectedItems] = useState<string[]>([]);

    const mounted = useRef<boolean>(true);

    const vgRef = useRef<ValidationGroup>(null);

    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const userId = params.get('id');

    const token = params.get('code');

    const { isAuthenticated } = useAuthentication();

    useEffect(() => {
        (async () => {
            const fontNames = fonts.map((font) => font.name);
            WebFont.load({
                google: {
                    families: fontNames,
                },
            });
        })();
    }, []);

    useEffect(() => {
        let canceled = false;

        if (actions?.onNeedIpAddress) {
            actions.onNeedIpAddress().then((ip) => {
                if (!canceled) {
                    setIpAddress(ip);
                }
            });
        }

        return () => {
            canceled = true;
        };
    }, [actions]);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            debouncedEmailValidation.cancel();
        };
    }, []);

    useEffect(() => {
        if (email) {
            handleEmailValidation(email);
        }
    }, [registerType]);

    const handlePrincipalRegister = async () => {
        /* istanbul ignore if */
        if (!validEmail(email) || !firstName || !lastName || !validPhone(mobilePhone)) {
            return;
        }

        setSubmitting(true);
        setErrorMessage('');
        try {
            const result = await actions.registerPrincipal(
                userId,
                email,
                firstName,
                middleName,
                lastName,
                mobilePhone,
                secondaryEmail,
                eSignFontId,
                ipAddress
            );

            // istanbul ignore else
            if (mounted) {
                setSubmitting(false);
                if (result.status >= 0) {
                    //Interpret the result
                    setTargetBaseUrl(result.baseUrl);
                    switch (result.status) {
                        case UserStore.Types.RegisterUserResultStatus.AccountExists:
                            setAccountExists(true);
                            break;
                        case UserStore.Types.RegisterUserResultStatus.RequestSuccessful:
                            setSuccessful(true);
                            break;
                        default:
                            setEmailUnavailable(true);
                            break;
                    }
                } else {
                    setErrorMessage('Principal Registration Failed.');
                }
            }
        } catch (err) {
            // istanbul ignore else
            if (mounted) {
                setSubmitting(false);
                setErrorMessage('Unable to complete Account registration.');
            }
        }
    };

    const handleBrokerRegister = async () => {
        /* istanbul ignore if */
        if (!validEmail(email) || !firstName || !lastName || !validPhone(mobilePhone)) {
            return;
        }

        setSubmitting(true);
        setErrorMessage('');
        try {
            const result = await actions.requestBrokerProfile(email, firstName, middleName, lastName, mobilePhone);
            // istanbul ignore else
            if (mounted) {
                setSubmitting(false);
                if (result.status >= 0) {
                    //Interpret the result
                    setTargetBaseUrl(result.baseUrl);
                    switch (result.status) {
                        case UserStore.Types.RegisterUserResultStatus.AccountExists:
                            setAccountExists(true);
                            break;
                        case UserStore.Types.RegisterUserResultStatus.RequestSuccessful:
                            setSuccessful(true);
                            break;
                        default:
                            setEmailUnavailable(true);
                            break;
                    }
                } else {
                    setErrorMessage('Broker Profile request failed.');
                }
            }
        } catch (err) {
            // istanbul ignore else
            if (mounted) {
                setSubmitting(false);
                setErrorMessage('Unable to complete Account registration.');
            }
        }
    };

    const handleEmailValidation = async (email) => {
        /* istanbul ignore if */
        if (!email) {
            return;
        }

        setAccountExists(false);
        setEmailUnavailable(false);
        setSubmitting(true);
        setErrorMessage('');
        try {
            const result = await actions.validateEmail(
                email,
                registerType === RegisterType.Applicant ? UserStore.Types.UserType.Principal : UserStore.Types.UserType.Broker
            );

            // istanbul ignore else
            if (mounted) {
                setSubmitting(false);
                if (result.status >= 0) {
                    //Interpret the result
                    setTargetBaseUrl(result.baseUrl);
                    switch (result.status) {
                        case UserStore.Types.ValidateEmailResultStatus.AccountExists:
                            setAccountExists(true);
                            break;
                        case UserStore.Types.ValidateEmailResultStatus.Unavailable:
                            setEmailUnavailable(true);
                            break;
                    }
                } else {
                    setErrorMessage('Registration Failed.');
                }
            }
        } catch (err) {
            // istanbul ignore else
            if (mounted) {
                setSubmitting(false);
                setErrorMessage('Unable to complete Account registration.');
            }
        }
    };

    const debouncedEmailValidation = debounce(handleEmailValidation, 500);

    useEffect(() => {
        setEmailUnavailable(false);
        setAccountExists(false);

        if (email) {
            const isValid = validEmail(email);
            if (isValid) {
                debouncedEmailValidation(email);
            }
        }
    }, [email]);

    useEffect(() => {
        if (!userId || !token) {
            return;
        }

        //Is an existing user so retrieve their profile details and let them edit them
        actions.getUserProfile(userId, token).then((profile) => {
            setEmail(profile.email ?? '');
            setFirstName(profile.firstName ?? '');
            setMiddleName(profile.middleInitial ?? '');
            setLastName(profile.lastName ?? '');
            setMobilePhone(profile.phone ?? '');
        });
    }, [userId, token, actions]);

    useEffect(() => {
        async function loadElectronicRecords() {
            const html = await getElectronicRecordsHtml();
            set_esignHtml(html);
        }

        loadElectronicRecords();
    }, []);

    const onConfirmAgreement = () => {
        setAgreementOpen(false);
        setAgreeToEsignAgreement(true);
    };

    const onRejectAgreement = () => {
        setAgreementOpen(false);
        setAgreeToEsignAgreement(false);
    };

    const onClickESignAgreement = async () => {
        setAgreementOpen(true);
    };

    const getLoginUrl = () => {
        switch (registerType) {
            case RegisterType.Broker:
                return `${targetBaseUrl}/login?email=${encodeURIComponent(email)}`;
            default:
                return `/login?email=${encodeURIComponent(email)}`;
        }
    };

    const getResetPasswordUrl = () => {
        switch (registerType) {
            case RegisterType.Broker:
                return `${targetBaseUrl}/forgotPassword?email=${encodeURIComponent(email)}`;
            default:
                return `/forgotPassword?email=${encodeURIComponent(email)}`;
        }
    };

    if (successful) {
        if (registerType === RegisterType.Broker) {
            return (
                <>
                    <Header />
                    <div className='app-container register'>
                        <div className={isMobileScreen() ? 'mobilePadding' : ''}>
                            <h2>Thank You</h2>
                        </div>
                        <main>
                            <div className='register-alternate-pages'>
                                Please keep an eye out for a registration link for the Broker Portal.
                            </div>
                        </main>
                    </div>
                </>
            );
        }
        if (registerType === RegisterType.Applicant) {
            return (
                <>
                    <Header />
                    <div className='app-container register'>
                        <div className={isMobileScreen() ? 'mobilePadding' : ''}>
                            <h2>Thank you for registering</h2>
                        </div>
                        <main>
                            <div className='register-alternate-pages'>Please check your email for the next steps...</div>
                        </main>
                    </div>
                </>
            );
        }
    }

    const isFormCompleted = () => {
        let inValid = false;
        if (registerType === RegisterType.Applicant) {
            inValid = !validEmail(email) || !firstName || !lastName || !validPhone(mobilePhone);
            if (secondaryEmail) {
                inValid = inValid || !validEmail(secondaryEmail);
            }
        } else {
            inValid = !validEmail(email) || !firstName || !lastName || !validPhone(mobilePhone);
        }

        if (inValid) {
            return false;
        }

        return true;
    };

    const canSubmit = () => {
        let inValid = !isFormCompleted();
        if (!inValid) {
            if (registerType === RegisterType.Applicant) {
                inValid = !acknowledgeNoPersonalLoans || emailUnavailable || accountExists;
            } else {
                inValid = emailUnavailable || accountExists;
            }
        }
        return inValid ? false : true;
    };

    const submitChanges = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const validation = vgRef.current?.instance?.validate();
        if (!validation?.isValid) {
            return;
        }

        if (registerType === RegisterType.Applicant) {
            handlePrincipalRegister();
        } else {
            handleBrokerRegister();
        }
    };

    if (isAuthenticated) {
        return (
            <>
                <Header />
                <div className='app-container register'>
                    <main>
                        <div className={isMobileScreen() ? 'mobilePadding' : ''}>
                            <HomePageLink />
                        </div>
                        <br />
                        <div className='register-alternate-pages'>Please log out to register a new account.</div>
                    </main>
                </div>
            </>
        );
    }

    const applicantBrokerButtonItems: ToggleButtonItem[] = [
        {
            id: 'Applicant',
            text: 'Applicant',
            hint: 'Select for Applicant',
        },
        {
            id: 'Broker',
            text: 'Broker',
            hint: 'Select for Broker',
        },
    ];

    const setToggleValues = (index: number, key: string) => {
        set_registerType(index);
        set_selectedItems([key]);
    };

    const panelChildren = () => {
        return (
            <main>
                <form
                    name='orderAppraisalForm'
                    onSubmit={(event) => submitChanges(event)}
                >
                    <ValidationGroup
                        id='vg1'
                        ref={vgRef}
                    >
                        <>
                            <div className='register subheader'>
                                <div className='sub-title'>I am a...</div>
                            </div>
                            <div>
                                <ToggleButton
                                    id='applicantBroker'
                                    items={applicantBrokerButtonItems}
                                    selectedItems={selectedItems}
                                    selectionMode={!selectedItems.length ? 'none' : 'single'}
                                    stylingMode='outlined'
                                    onItemClick={(e) => setToggleValues(e?.itemIndex + 1, e?.itemData.id)}
                                    width={'100%'}
                                    height='40px'
                                />
                            </div>

                            {registerType !== RegisterType.NotSet && (
                                <>
                                    <div className='login_register-fields'>
                                        <EmailBox
                                            id='emailAddress'
                                            data-testid='register-page-email-address'
                                            name='emailAddress'
                                            value={email}
                                            disabled={!!userId}
                                            onChange={(email: string) => setEmail(email)}
                                            label='Email Address*'
                                            required={true}
                                        />
                                    </div>
                                    {emailUnavailable && (
                                        <Panel
                                            theme={PanelTheme.Red}
                                            borderRadius={PanelBorderRadius.Medium}
                                            padding={30}
                                        >
                                            <b>Sorry, but this email address is not available for registration.</b>
                                        </Panel>
                                    )}
                                    {accountExists && (
                                        <Panel
                                            theme={PanelTheme.Red}
                                            borderRadius={PanelBorderRadius.Medium}
                                            padding={30}
                                        >
                                            <div>
                                                <b>
                                                    An account with this email already exists. Please select from one of the
                                                    options below:
                                                </b>
                                                <ul>
                                                    <li style={{ color: 'grey' }}>
                                                        <Button
                                                            id='login-url-button'
                                                            key='login-url-button'
                                                            theme={ButtonTheme.LinkBlue}
                                                            handleClick={() => navigate(getLoginUrl())}
                                                        >
                                                            Go to Login page
                                                        </Button>
                                                    </li>
                                                    <li style={{ color: 'grey' }}>
                                                        <Button
                                                            id='password-reset-button'
                                                            key='password-reset-button'
                                                            theme={ButtonTheme.LinkBlue}
                                                            handleClick={() => navigate(getResetPasswordUrl())}
                                                        >
                                                            Request Password Reset
                                                        </Button>
                                                    </li>
                                                    <li style={{ color: 'grey' }}>
                                                        <Button
                                                            id='password-reset-button'
                                                            key='password-reset-button'
                                                            theme={ButtonTheme.LinkBlue}
                                                            disabled={true}
                                                        >
                                                            Change the email address
                                                        </Button>
                                                    </li>
                                                </ul>
                                            </div>
                                        </Panel>
                                    )}
                                    <div>
                                        <TextBox
                                            id='firstName'
                                            data-testid='register-page-first-name'
                                            value={firstName}
                                            maxLength={50}
                                            required={true}
                                            label='Legal First Name*'
                                            onChange={(value) => setFirstName(value)}
                                            validator={<RequiredRule message='First Name is required' />}
                                        ></TextBox>
                                    </div>
                                    <div>
                                        <TextBox
                                            id='middleName'
                                            data-testid='register-page-middle-name'
                                            value={middleName}
                                            maxLength={50}
                                            onChange={(value) => setMiddleName(value)}
                                            label='Middle Initial or Name'
                                        ></TextBox>
                                    </div>
                                    <div>
                                        <TextBox
                                            id='lastName'
                                            data-testid='register-page-last-name'
                                            value={lastName}
                                            label='Legal Last Name*'
                                            maxLength={50}
                                            onChange={(value) => setLastName(value)}
                                            validator={<RequiredRule message='Last Name is required' />}
                                        ></TextBox>
                                    </div>
                                    <div>
                                        <TextBox
                                            id='mobilePhone'
                                            data-testid='register-page-mobile-phone'
                                            type='tel'
                                            mask='+1 (000) 000-0000'
                                            maskInvalidMessage='The phone must have a correct USA phone format'
                                            value={mobilePhone}
                                            label='Mobile Phone Number*'
                                            required={true}
                                            onChange={(phone) => setMobilePhone(phone)}
                                            validator={<RequiredRule message='Phone # is required' />}
                                        ></TextBox>
                                    </div>
                                    {registerType === RegisterType.Applicant && (
                                        <>
                                            <div>
                                                <EmailBox
                                                    id='secondaryEmail'
                                                    data-testid='register-page-secondary-email'
                                                    name='secondaryEmail'
                                                    value={secondaryEmail}
                                                    disabled={!!userId}
                                                    onChange={(email: string) => setSecondaryEmail(email)}
                                                    label='Secondary Email Address'
                                                />
                                            </div>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    paddingBottom: '1rem',
                                                }}
                                            >
                                                <div>
                                                    <input
                                                        id='acknowledgeNoPersonalLoans'
                                                        data-testid='register-page-acknowledge-no-personal-loans'
                                                        type='checkbox'
                                                        checked={acknowledgeNoPersonalLoans}
                                                        required
                                                        onChange={(e) => setAcknowledgeNoPersonalLoans(e.target.checked)}
                                                        disabled={!isFormCompleted()}
                                                    />
                                                </div>
                                                <div
                                                    style={{
                                                        marginLeft: '10px',
                                                    }}
                                                >
                                                    <label
                                                        htmlFor='acknowledgeNoPersonalLoans'
                                                        style={{
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        I acknowledge that this application is for a BUSINESS PURPOSE
                                                        <br />
                                                        AND that the property is or will be OWNED BY A CORPORATE ENTITY.
                                                    </label>
                                                </div>
                                            </div>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                }}
                                            >
                                                <div>
                                                    <input
                                                        id='agreeToEsignAgreement'
                                                        data-testid='register-page-agree-to-esign-agreement'
                                                        type='checkbox'
                                                        checked={agreeToEsignAgreement}
                                                        required
                                                        onChange={(e) => setAgreeToEsignAgreement(e.target.checked)}
                                                        onClick={() => onClickESignAgreement()}
                                                        disabled={!isFormCompleted() || !acknowledgeNoPersonalLoans}
                                                    />
                                                </div>
                                                <div
                                                    style={{
                                                        marginLeft: '10px',
                                                    }}
                                                >
                                                    <label
                                                        htmlFor='agreeToEsignAgreement'
                                                        style={{
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        Consent to Use Electronic Records and Disclosures. By checking this check
                                                        box and clicking "REGISTER", I acknowledge that I have read and agree on
                                                        behalf of the Business Entities to the Consent to Use Electronic Records
                                                        and Disclosures.
                                                    </label>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {errorMessage && (
                                        <div style={{ marginTop: '1rem' }}>
                                            <Panel
                                                theme={PanelTheme.Red}
                                                borderRadius={PanelBorderRadius.Medium}
                                                padding={20}
                                            >
                                                <div className='register-error'>
                                                    <span>
                                                        {errorMessage}
                                                        <br />
                                                    </span>
                                                    <span>Please try again later.</span>
                                                </div>
                                            </Panel>
                                        </div>
                                    )}

                                    {registerType === RegisterType.Applicant && (
                                        <div className='button-group'>
                                            <Button
                                                id='register-page-applicant-button'
                                                dataTestid='register-page-button'
                                                theme={ButtonTheme.Primary}
                                                disabled={
                                                    submitting ||
                                                    !(agreeToEsignAgreement && acknowledgeNoPersonalLoans) ||
                                                    !canSubmit()
                                                }
                                                type='submit'
                                                width='90%'
                                            >
                                                Register
                                            </Button>
                                        </div>
                                    )}
                                    {registerType === RegisterType.Broker && (
                                        <div className='button-group'>
                                            <Button
                                                id='register-page-broker-button'
                                                dataTestid='register-page-button'
                                                theme={ButtonTheme.Primary}
                                                disabled={submitting || !canSubmit()}
                                                type='submit'
                                                width='90%'
                                            >
                                                Register Broker Profile
                                            </Button>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    </ValidationGroup>
                </form>
            </main>
        );
    };

    const ElectronicAgreementBody = () => <div dangerouslySetInnerHTML={{ __html: esignHtml }}></div>;

    return (
        <div className='app-container'>
            <Header />
            <div className='app-container authenticationContainer'>
                {agreementOpen ? (
                    <SignaturePage
                        agreementBody={<ElectronicAgreementBody />}
                        firstName={firstName}
                        lastName={lastName}
                        onSubmitAgreement={onConfirmAgreement}
                        onDeclineAgreement={onRejectAgreement}
                    />
                ) : isMobileScreen() ? (
                    <div className='authenticationContainer mobile'>
                        <h1 style={{ paddingBottom: '1.5rem' }}>Register Your Account</h1>
                        {panelChildren()}
                    </div>
                ) : (
                    <div className='authenticationContainer desktop'>
                        <h1 style={{ paddingBottom: '1.5rem' }}>Register Your Account</h1>
                        <Panel
                            theme={PanelTheme.White}
                            borderRadius={PanelBorderRadius.Medium}
                            width={'90%'}
                        >
                            <div className='authenticationContainer desktop desktopWrapper'>{panelChildren()}</div>
                        </Panel>
                    </div>
                )}
            </div>
        </div>
    );
};

export const mapDispatchToProps = (dispatch: Store.ApplyDispatchType) => {
    const props: { actions: Actions; dispatch: Store.ApplyDispatchType } = {
        actions: {
            validateEmail: (email: string, userType: UserStore.Types.UserType) =>
                dispatch(Store.CrossStore.validateEmail(email, userType)),
            registerPrincipal: (
                id: string,
                email: string,
                firstName: string,
                middleName: string,
                lastName: string,
                mobilePhone: string,
                secondaryEmail: string,
                eSignFontId: number,
                ipAddress: string
            ) =>
                dispatch(
                    Store.CrossStore.register(
                        id,
                        email,
                        firstName,
                        middleName,
                        lastName,
                        mobilePhone,
                        secondaryEmail,
                        eSignFontId,
                        ipAddress
                    )
                ),
            requestBrokerProfile: (email: string, firstName: string, middleName: string, lastName: string, mobilePhone: string) =>
                dispatch(Store.CrossStore.requestBrokerProfile(email, firstName, middleName, lastName, mobilePhone)),
            onNeedIpAddress: () => dispatch(Store.CrossStore.getIpAddress()),
            getUserProfile: (id: string, token: string) => dispatch(Store.CrossStore.getUserProfile(id, token)),
        },
        dispatch,
    };
    return props;
};

export default connect(null, mapDispatchToProps)(RegisterPageV2);
